import { createLinkedAbortController } from './createLinkedAbortController'
import { Abortable, TAbortable } from './Abortable'
import { okAsync, Result, ResultAsync } from 'neverthrow'

export type TDebounce = <T, E, A extends unknown[]>(
  f: TDebounceable<T, E, A>,
) => TDebounced<T, E, A>

export type TDebounceable<T, E, A extends unknown[]> = (
  args: A,
  opts?: TDebounceOpts,
) => ResultAsync<T, E>

export type TDebouncedFn<T, E, A extends unknown[]> = (
  ...args: A
) => TAbortable<T, E>
export type TDebounced<T, E, A extends unknown[]> = {
  options: (opts: TDebounceOpts) => TDebouncedFn<T, E, A>
  call: TDebouncedFn<T, E, A>
  abort: (reason?: unknown) => void
  flush: () => ReturnType<TDebouncedFn<T | null, E, A>>
}

export type TDebounceOpts = {
  signal?: AbortSignal
}

export const debounce = <T, E, A extends unknown[]>(
  f: TDebounceable<T, E, A>,
  delay: number = 500,
): TDebounced<T, E, A> => {
  let pendingCall: {
    timeout?: NodeJS.Timeout
    controller: AbortController
    call: () => ReturnType<TDebouncedFn<T, E, A>>
    flush: TDebounced<T, E, A>['flush']
  } | null = null

  const abort = (reason?: unknown): void => {
    clearTimeout(pendingCall?.timeout)
    pendingCall?.controller?.abort(reason)
    pendingCall = null
  }

  const debouncedFn = (args: A, opts?: TDebounceOpts): TAbortable<T, E> => {
    if (pendingCall !== null) abort()

    const controller = createLinkedAbortController(opts?.signal)
    pendingCall = {
      controller,
      call,
      flush: () => call(),
    }

    function call(delay?: number): ReturnType<TDebouncedFn<T, E, A>> {
      const promise = new Promise<Result<T, E>>(resolve => {
        if (delay === undefined) callImmediate()
        else callDelayed(delay)

        function callImmediate(): void {
          if (pendingCall !== null) pendingCall.timeout = undefined

          try {
            void f(args, mergedOpts).then(resolve)
          } finally {
            pendingCall = null
          }
        }

        function callDelayed(delay: number): void {
          if (pendingCall === null) return

          pendingCall.timeout = setTimeout(() => {
            callImmediate()
          }, delay)
        }
      })

      return Abortable.from(new ResultAsync(promise), controller.signal)
    }

    const mergedOpts = {
      ...opts,
      signal: pendingCall.controller.signal,
    }

    return call(delay)
  }

  return {
    options:
      opts =>
      (...args: A) =>
        debouncedFn(args, opts),
    call: (...args: A) => {
      return debouncedFn(args)
    },
    abort,
    flush: () => {
      if (pendingCall === null) return okAsync(null)
      return pendingCall?.flush?.()
    },
  }
}
